const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type DestinationLink =
    | {
          readonly to: '/caregivers';
          readonly search: {
              readonly caregiverId: string;
              readonly organizationId: string;
          };
      }
    | {
          readonly to: '/clients';
          readonly search: {
              readonly clientId: string;
              readonly organizationId: string;
          };
      }
    | {
          readonly to: '/admin/organizations/$organization-id';
          readonly params: { readonly 'organization-id': string };
      };

/**
 * The note fields destination resolution needs. Structural so the admin note
 * read model and the org-scoped one both satisfy it without either importing
 * the other's shape.
 */
export type DestinationNote = {
    readonly organization_id: string;
    readonly caregiver?: { readonly id: string } | null;
    readonly client?: { readonly id: string } | null;
};

export type DestinationItem = {
    readonly entity_refs: ReadonlyArray<{
        readonly entity_type: string;
        readonly id_hint?: string | null;
    }>;
};

/**
 * The caregiver and client a routed item is about.
 *
 * `id_hint` is LLM-extracted free text, so it is only trusted as an id when it
 * is actually a UUID; otherwise the note's own resolved entity wins.
 */
function resolveEntityIds(
    note: DestinationNote,
    item?: DestinationItem,
): {
    readonly caregiverId: string | undefined;
    readonly clientId: string | undefined;
} {
    const clientHint = item?.entity_refs.find((ref) =>
        ref.entity_type.toLowerCase().includes('client'),
    )?.id_hint;
    const caregiverHint = item?.entity_refs.find((ref) =>
        ref.entity_type.toLowerCase().includes('caregiver'),
    )?.id_hint;
    return {
        clientId:
            (clientHint && UUID_PATTERN.test(clientHint) ? clientHint : null) ??
            note.client?.id,
        caregiverId:
            (caregiverHint && UUID_PATTERN.test(caregiverHint)
                ? caregiverHint
                : null) ?? note.caregiver?.id,
    };
}

/** Org-scoped app link for a caregiver (deep link switches org context). */
export function caregiverAppLink(
    caregiverId: string,
    organizationId: string,
): DestinationLink {
    return {
        to: '/caregivers',
        search: { caregiverId, organizationId },
    };
}

/** Org-scoped app link for a client (deep link switches org context). */
export function clientAppLink(
    clientId: string,
    organizationId: string,
): DestinationLink {
    return {
        to: '/clients',
        search: { clientId, organizationId },
    };
}

/**
 * Best-effort link to where a routed item's change surfaced, in the
 * org-scoped product UI: caregiver tables open the caregiver's page in
 * their org (availability, tags, memory all live there), client tables the
 * client's, and org recommendation defaults the admin organization page.
 * Playbook and agency-context rows return null; their caller handles the
 * same-page modal or noninteractive label. Other rows return null when the
 * destination has no detail surface or the item/note lacks the entity ref.
 */
export function destinationLink(
    resultRefTable: string | null,
    note: DestinationNote,
    item?: DestinationItem,
): DestinationLink | null {
    if (!resultRefTable) {
        return null;
    }
    const isClientTable = resultRefTable.includes('client');
    const isCaregiverTable =
        resultRefTable.startsWith('caregiver') ||
        resultRefTable.startsWith('org_caregiver');

    const { caregiverId, clientId } = resolveEntityIds(note, item);

    if (isClientTable && clientId) {
        return clientAppLink(clientId, note.organization_id);
    }
    if (isCaregiverTable && caregiverId) {
        return caregiverAppLink(caregiverId, note.organization_id);
    }
    if (
        resultRefTable.startsWith('agent_playbook') ||
        resultRefTable.startsWith('organization_agency_context')
    ) {
        return null;
    }
    if (
        resultRefTable.startsWith('organization') ||
        resultRefTable.startsWith('org_recommendation')
    ) {
        return {
            to: '/admin/organizations/$organization-id',
            params: { 'organization-id': note.organization_id },
        };
    }
    return null;
}

// Ordered by specificity: first matching needle wins.
const DESTINATION_TYPE_LABELS: ReadonlyArray<readonly [string, string]> = [
    ['caregiver_availability', 'Availability window'],
    ['caregiver_client_preference', 'Client preference'],
    ['caregiver_tag', 'Caregiver tag'],
    ['client_caregiver_preference', 'Client preference'],
    ['memories', 'Memory'],
    ['playbook', 'Playbook rule'],
    ['recommendation', 'Recommendation default'],
    ['agency', 'Agency context'],
    ['outreach_filter', 'Outreach filter'],
    ['organization', 'Organization settings'],
];

/** Human name for where a routed item landed, e.g. "Availability window". */
export function destinationTypeLabel(resultRefTable: string): string {
    for (const [needle, label] of DESTINATION_TYPE_LABELS) {
        if (resultRefTable.includes(needle)) {
            return label;
        }
    }
    const words = resultRefTable.split('_').join(' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}
